import React, { useState, useEffect, useContext } from 'react';

import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';

import API from 'libs/api';

import {
  DEFAULT_WIDGET_TYPE,
  SINGLE_IMAGE_MAX_FILES_ALLOWED,
  MULTIPLE_IMAGE_MAX_FILES_ALLOWED,
} from './EditImageBlock.constants';
import {
  updateImageConfiguration,
  createImageConfiguration,
  updateImageElementFromSave,
  isSingleImageWidgetType,
  getContentStyle,
  getImageStyle,
} from './EditImageBlock.utils';
import {
  ImageWidgetSelector,
  UploadedImageList,
  ImageUploadWell,
  SingleImageContent,
} from './components';
import SingleImageStyle from './components/SingleImageStyle';
import {
  BASE_SINGLE_IMAGE_CONTENT_DEFAULT_STYLES,
  BASE_SINGLE_IMAGE_DEFAULT_STYLES,
} from './components/SingleImageStyle/SingleImageStyle.constants';
import { useLatestRefValue } from '../../../../../utils/hooks';
import {
  IMAGE_BLOCK_ELEMENT_TYPE,
  IMAGE_COMPONENT_NAME,
  TEXT_BLOCK_ELEMENT_TYPE,
} from '../../../../constants/types';
import { ImageBlockPropType } from '../../../../types/image-block.types';
import { EditPageElementModalContext } from '../../EditPageElementModal.context';
import {
  createContentConfiguration,
  updateContentConfiguration,
  updateContentElementFromSave,
} from '../EditTextBlock/EditTextBlock.utils';

import './EditImageBlock.scss';

const propTypes = {
  element: ImageBlockPropType.isRequired,
};

const defaultProps = {};

export default function EditImageBlock({ element }) {
  const {
    images: initialImages = [],
    widgetType: initialWidgetType = DEFAULT_WIDGET_TYPE,
    children = [],
  } = element;
  const {
    page,
    registerCallback,
    onUpdateElementState,
    addValidationError,
    clearValidationErrors,
    activeRecipeName,
  } = useContext(EditPageElementModalContext);

  const [widgetType, setWidgetType] = useState(initialWidgetType);
  const [images, setImages] = useState(initialImages);

  const latestElementRef = useLatestRefValue(element);
  const latestWidgetTypeRef = useLatestRefValue(widgetType);
  const latestImagesRef = useLatestRefValue(images);

  function onDeleteImage(imageId) {
    const currentElement = latestElementRef.current;
    const nextImages = images.filter(({ id }) => id !== imageId);

    setImages(nextImages);
    onUpdateElementState(IMAGE_BLOCK_ELEMENT_TYPE, { ...currentElement, images: nextImages });
  }

  function onSelectImages(nextImages) {
    const currentElement = latestElementRef.current;

    setImages(nextImages);
    onUpdateElementState(IMAGE_BLOCK_ELEMENT_TYPE, { ...currentElement, images: nextImages });
  }

  function onSelectWidget(nextWidgetType) {
    const currentElement = latestElementRef.current;
    const { images: currentImages = [], style: currentStyle = {} } = currentElement;

    setWidgetType(nextWidgetType);
    const finalStyle =
      isSingleImageWidgetType(nextWidgetType) && _isEmpty(currentStyle)
        ? BASE_SINGLE_IMAGE_DEFAULT_STYLES
        : currentStyle;
    const finalImages =
      isSingleImageWidgetType(nextWidgetType) && !_isEmpty(currentImages)
        ? [currentImages[0]]
        : currentImages;

    onUpdateElementState(IMAGE_BLOCK_ELEMENT_TYPE, {
      ...currentElement,
      widgetType: nextWidgetType,
      style: finalStyle,
      images: finalImages,
    });
  }

  function getAllStyle({ withFixedStyles = true } = {}) {
    const imageStyle = _get(element, 'style', {});
    const markdownContent = _get(children, '[0].content', '');
    const hasMarkdownContent = !_isEmpty(markdownContent);
    const finalImageStyle = getImageStyle(activeRecipeName, imageStyle, {
      getFixedStyles: withFixedStyles,
      isBackground: hasMarkdownContent,
    });
    const markdownStyle = _get(children, '[0].style', {});
    const finalMarkdownStyle = getContentStyle(activeRecipeName, markdownStyle, withFixedStyles);
    return { ...finalImageStyle, ...finalMarkdownStyle };
  }

  function getMarkdownChildren() {
    const currentElement = latestElementRef.current;
    const name = `${_get(images, '[0].filename', '')} Image Content`;

    const markdownChildren = _get(currentElement, 'children[0]', {
      type: TEXT_BLOCK_ELEMENT_TYPE,
      contentName: name,
    });
    return markdownChildren;
  }

  function onMarkdownChange(value) {
    const currentElement = latestElementRef.current;
    const markdownChildren = getMarkdownChildren();
    const markdownContent = _get(markdownChildren, 'content', '');
    let style = _get(markdownChildren, 'style', {});

    if (_isEmpty(markdownContent) && !_isEmpty(value)) {
      // This is a new markdown content
      style = getContentStyle(activeRecipeName, BASE_SINGLE_IMAGE_CONTENT_DEFAULT_STYLES);
    }

    onUpdateElementState(IMAGE_BLOCK_ELEMENT_TYPE, {
      ...currentElement,
      children: [
        {
          ...markdownChildren,
          content: value,
          style,
        },
      ],
    });
  }

  function onStyleChange(e, { value }) {
    const currentElement = latestElementRef.current;
    const markdownChildren = getMarkdownChildren();
    const markdownContent = _get(markdownChildren, 'content', '');
    const hasMarkdownContent = !_isEmpty(markdownContent);
    const contentStyle = getContentStyle(activeRecipeName, value);
    const imageStyle = getImageStyle(activeRecipeName, value, { isBackground: hasMarkdownContent });
    onUpdateElementState(IMAGE_BLOCK_ELEMENT_TYPE, {
      ...currentElement,
      style: imageStyle,
      children: [
        {
          ...markdownChildren,
          style: contentStyle,
        },
      ],
    });
  }

  async function onSaveMarkdownContent(markdownState) {
    const { id: pageId } = page;
    const { componentConfigurationId, contentNodeConfigurationId, contentName, content } =
      markdownState;

    if (contentNodeConfigurationId && componentConfigurationId) {
      if (_isEmpty(content)) {
        await API.removeCustomPageContent(componentConfigurationId);
        return undefined;
      }
      await updateContentConfiguration(componentConfigurationId, contentNodeConfigurationId, {
        content,
        contentName,
      });
      return markdownState;
    }
    if (_isEmpty(content)) {
      return undefined;
    }
    const result = await createContentConfiguration(contentName, content, pageId);
    return updateContentElementFromSave(markdownState, result);
  }

  async function onSaveContent() {
    const currentElement = latestElementRef.current;
    const currentImages = latestImagesRef.current;
    const currentWidgetType = latestWidgetTypeRef.current;
    const { componentConfigurationId, children: currentElementChildren } = currentElement;
    const { id: pageId } = page;

    const markdownState = _get(currentElementChildren, '[0]');
    const childrenResult = [];
    if (markdownState) {
      if (isSingleImageWidgetType(currentWidgetType)) {
        const markdownResult = await onSaveMarkdownContent(markdownState);
        if (markdownResult) {
          childrenResult.push(markdownResult);
        }
      } else {
        // We are saving a multiple image component
        const { componentConfigurationId: markdownComponentId } = markdownState;
        if (markdownComponentId) {
          await API.removeCustomPageContent(markdownComponentId);
        }
      }
    }

    currentElement.children = childrenResult;

    if (!isSingleImageWidgetType(currentWidgetType)) {
      currentElement.style = {};
    }

    if (componentConfigurationId) {
      const result = await updateImageConfiguration(
        componentConfigurationId,
        currentImages,
        currentWidgetType,
      );

      return updateImageElementFromSave(currentElement, result);
    }

    const result = await createImageConfiguration(currentImages, currentWidgetType, pageId);
    return updateImageElementFromSave(currentElement, result);
  }

  useEffect(() => {
    clearValidationErrors();
    if (_isEmpty(images)) {
      addValidationError('At least one image is required');
    }
  }, [images]);

  useEffect(() => {
    registerCallback(IMAGE_BLOCK_ELEMENT_TYPE, onSaveContent);
  }, []);

  const oneImageAllowed = widgetType === IMAGE_COMPONENT_NAME;
  const markdownContent = _get(children, '[0].content', '');
  const hasMarkdownContent = !_isEmpty(markdownContent) && oneImageAllowed;
  const finalStyle = getAllStyle({ withFixedStyles: false });

  return (
    <div className="edit-image-block">
      <ImageWidgetSelector selectedType={widgetType} onSelect={onSelectWidget} />
      <UploadedImageList
        images={images}
        numDisplay={oneImageAllowed ? 1 : null}
        onDeleteImage={onDeleteImage}
      />

      <ImageUploadWell
        images={images}
        onSelectImages={onSelectImages}
        maxFilesAllowed={
          oneImageAllowed ? SINGLE_IMAGE_MAX_FILES_ALLOWED : MULTIPLE_IMAGE_MAX_FILES_ALLOWED
        }
      />

      {oneImageAllowed && (
        <>
          <SingleImageStyle
            isBackground={hasMarkdownContent}
            style={finalStyle}
            onChange={onStyleChange}
            recipeName={activeRecipeName}
          />
          <SingleImageContent content={markdownContent} onMarkdownChange={onMarkdownChange} />
        </>
      )}
    </div>
  );
}

EditImageBlock.propTypes = propTypes;
EditImageBlock.defaultProps = defaultProps;
